import { Box } from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import * as React from "react";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { MainNavigationPanel } from "./main-navigation-panel/MainNavigationPanel";
import style from "./navigation-panel.scss";
import SubNavigationPanel from "./sub-navigation-panel/SubNavigationPanel";
import { detectApplicationFromPath, HOME_ROUTE, INTEGRATIONS_ROUTE } from "components/router/Routes";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { tenantHasFeatureLicense } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { Application, setApplication } from "store/application";
import { useIsDarkThemeActive } from "utils/useTheme";

interface Props {
    onSubNavigationOpenChange: (value: boolean) => void;
    isSubNavigationOpened: boolean;
    isMobileMainNavigationOpen: boolean;
    isTenantBannerVisible: boolean;
    isTrialTenant?: boolean;
}

const connector = connect((state: StoreState) => ({
    user: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
    theme: state.themeReducer.theme,
}));

const NavigationPanel = (props: Props & ConnectedProps<typeof connector>): JSX.Element => {
    const dispatch = useDispatch();
    const application = useSelector((state: StoreState) => state.applicationReducer.selected);
    const history = useHistory();
    const { pathname } = useLocation();
    const isAssetManagementFlagActive = useFeature(FLAG_ASSET_MANAGEMENT);

    const isDarkMode = useIsDarkThemeActive();

    function isBamAvailable() {
        const hasFeatureLicense = tenantHasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
        return isAssetManagementFlagActive && hasFeatureLicense;
    }

    const [windowSize, setWindowSize] = React.useState({
        width: window.innerWidth,
    });

    React.useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [windowSize.width]);

    function isOnBam() {
        return application === Application.BAM;
    }

    React.useEffect(() => {
        // TODO BCC-5887 How to check if there is no BMP access?
        if (!isBamAvailable() && isOnBam()) {
            handleSetApplication(Application.BMP);
        }
    }, []);

    React.useEffect(() => {
        const currentApplication = detectApplicationFromPath(pathname);
        if (currentApplication !== undefined && currentApplication !== application) {
            if (currentApplication === Application.BAM && !isBamAvailable()) {
                return;
            }
            // In BMP+assets mode the asset routes are embedded in BMP navigation,
            // so navigating to /assets/* should not switch the app to BAM.
            if (
                currentApplication === Application.BAM &&
                application === Application.BMP &&
                isAssetManagementFlagActive
            ) {
                return;
            }
            dispatch(setApplication(currentApplication));
        }
    }, [pathname, isAssetManagementFlagActive]);

    const handleSetApplication = (application: Application) => {
        dispatch(setApplication(application));
        if (application === Application.BAM) {
            history.push(INTEGRATIONS_ROUTE.path);
        } else {
            history.push(HOME_ROUTE.path);
        }
    };

    return (
        <>
            <Box className={style.restructuredNavigationPanel}>
                <MainNavigationPanel
                    onSubNavigationOpenChange={props.onSubNavigationOpenChange}
                    isSubNavigationOpened={props.isSubNavigationOpened}
                    isMobileMainNavigationOpen={props.isMobileMainNavigationOpen}
                />

                <SubNavigationPanel
                    onSubNavigationOpenChange={props.onSubNavigationOpenChange}
                    isSubNavigationOpened={props.isSubNavigationOpened}
                    isDarkMode={isDarkMode}
                />
            </Box>
        </>
    );
};

const connectedSideNavigation = connector(NavigationPanel);
export { connectedSideNavigation as NavigationPanel };
